import { isEqual } from "lodash";
import { jwtDecode } from "jwt-decode";
import { deepTrimValue } from "tools/common/trimFormValues";
import {
  CredentialsFormState,
  GoogleIdentity,
  ValidateExhibitorResponse,
} from "view/SignUp/SignUpToken/context/types";
import { PersistedSignUpForm } from "view/SignUp/SignUpToken/context/persistence";
import applyGoogleEmail from "view/SignUp/SignUpToken/utils/applyGoogleEmail";

const isUnexpired = (identity: GoogleIdentity, now: number): boolean => {
  try {
    const { exp } = jwtDecode<{ exp?: number }>(identity.identityToken);

    return exp === undefined || exp * 1000 > now;
  } catch {
    return false;
  }
};

/**
 * How the first step picks up after a trip to Google.
 *
 * Google returned an account: it replaces whatever was connected and its
 * address replaces the typed one, so any earlier verdict is about another
 * combination and the step validates again.
 *
 * It did not — the person cancelled, or pressed Back on Google's own page: the
 * step resumes exactly as it was left, with the account that was connected
 * before (`Change Google Account`), the same verdict, and validates again only
 * if the fields differ from what was last validated. Validating needlessly
 * would cost the person what the second step holds.
 */
export const resumeCredentials = (
  form: Pick<
    PersistedSignUpForm,
    | "credentials"
    | "validatedCredentials"
    | "validationResult"
    | "googleIdentity"
  >,
  returnedIdentity: GoogleIdentity | null,
  now: number = Date.now()
): {
  identity: GoogleIdentity | null;
  credentials: CredentialsFormState;
  validationResult: ValidateExhibitorResponse | null;
  mustValidate: boolean;
} => {
  const typed: CredentialsFormState = deepTrimValue(form.credentials);

  if (returnedIdentity) {
    return {
      identity: returnedIdentity,
      credentials: applyGoogleEmail(typed, returnedIdentity),
      validationResult: null,
      mustValidate: true,
    };
  }

  const kept =
    form.googleIdentity && isUnexpired(form.googleIdentity, now)
      ? form.googleIdentity
      : null;
  const credentials = applyGoogleEmail(typed, kept);

  // The account connected before the trip lapsed meanwhile: the verdict was
  // given for it, and the API would now refuse its token.
  if (form.googleIdentity && !kept) {
    return {
      identity: null,
      credentials,
      validationResult: null,
      mustValidate: true,
    };
  }

  return {
    identity: kept,
    credentials,
    validationResult: form.validationResult,
    mustValidate: !isEqual(credentials, form.validatedCredentials),
  };
};

export default resumeCredentials;
